import { useMemo } from "react";

import { toCount, toDollars } from "common/utils/formatting";
import { pluralized } from "common/utils/strings";
import Navigate from "common/router/Navigate";
import Image from "common/components/Image";
import { OrderItemStatus, ApprovalNeededReason } from "common/types/orders";

import Page from "client/common/components/layouts/Page";
import { useClientQuery } from "client/accounts/hooks";
import LoadingPage from "client/common/components/loading/LoadingPage";
import TextButton from "client/common/components/buttons/TextButton";
import Body from "client/common/components/layouts/Body";
import { ClientCheckoutOrder } from "client/orders/types";
import Timeline, { Status, TimelinePropEventOpts } from "client/common/components/content/Timeline";
import { IClient } from "client/accounts/types";
import { ModalStatus } from "client/common/types";
import { useClinic } from "client/clinics/hooks";
import MapBox from "client/common/components/content/MapBox";
import { urls } from "client/routes";

import checkoutComplete from "../images/checkout-complete.svg";
import { useLastCheckoutQuery } from "../hooks";

import styles from "../styles/OrderConfirmationPage.module.less";

type OrderTimelineProps = {
  order: ClientCheckoutOrder;
  client: IClient;
};

const OrderTimeline = ({ order, client }: OrderTimelineProps) => {
  const clinic = useClinic();
  const orderMarkers = useMemo(
    () => (order.coordinates ? [order.coordinates] : []),
    [order.coordinates],
  );

  // Helper values:
  // We should likely surface most of these from the backend, but that can come when we move the
  // timeline to the backend as well
  const itemNames =
    order.order_items.length === 1
      ? order.order_items[0].item.product.product_line.name
      : toCount(order.order_items.length, "item");

  const hasPrescriptionItems = order.order_items.some(
    (oi) => oi.item.product.product_line.prescription_required,
  );

  const needsHighRiskConfirmation = order.order_items.some(
    (oi) =>
      oi.status === OrderItemStatus.ApprovalNeeded &&
      oi.approval_needed_reason === ApprovalNeededReason.HIGH_RISK_CLIENT,
  );

  // Check that no items need approval (defensive against post-approval statuses)
  const hasAllItemsApproved = order.order_items.every(
    (oi) => oi.status !== OrderItemStatus.ApprovalNeeded,
  );

  const orderItemsWithValidPrescriptions = order.order_items.flatMap((orderItem) =>
    orderItem.prescription_fill?.is_approved ? [orderItem] : [],
  );

  const validPrescriptionNames =
    orderItemsWithValidPrescriptions.length === 1
      ? `from ${orderItemsWithValidPrescriptions[0].prescription_fill?.prescribed_by_name} for ${orderItemsWithValidPrescriptions[0].patient?.name_display}`
      : "on all prescriptions";

  const { order_items: orderItems } = order;
  const shippingMethodToOrderItemDict = orderItems.reduce<Record<string, string[]>>(
    (acc, orderItem) => {
      const shippingMethod = orderItem.shipping_method_display;
      if (!shippingMethod) {
        return acc;
      }
      const patientDisplay = orderItem.patient?.name_display
        ? ` for ${orderItem.patient.name_display}`
        : "";
      const itemLabel = `${orderItem.item.product.product_line.name} (${orderItem.quantity})${patientDisplay}`;
      (acc[shippingMethod] ??= []).push(itemLabel);
      return acc;
    },
    {},
  );

  const shippingMethods = Object.keys(shippingMethodToOrderItemDict);

  const events: TimelinePropEventOpts[] = [
    {
      key: "request",
      label: "Request Medication",
      header: `You've successfully requested ${itemNames}`,
      content: (
        <div className={styles.request}>
          <div>
            You paid <strong>{toDollars(order.total)}</strong> in total.
          </div>
          <TextButton
            className={styles.link}
            icon="right"
            dest={urls.orders.detail({ params: { id: order.id } })}
          >
            View order details
          </TextButton>
        </div>
      ),
      status: "complete",
    },
    ...(hasPrescriptionItems || needsHighRiskConfirmation
      ? [
          {
            key: "review",
            label: "Veterinary Review",
            content:
              "Note that your card will not be charged until your veterinarian approves your order.",
            ...(hasAllItemsApproved
              ? {
                  header: hasPrescriptionItems
                    ? `Awesome news! We have a valid prescription on file ${validPrescriptionNames}`
                    : "Your order is approved and ready to be fulfilled", // this should never happen. a high risk client cant be approved on checkout
                  status: "complete" as Status,
                }
              : {
                  header: hasPrescriptionItems
                    ? `Your veterinarian will need to approve prescriptions in this order before fulfillment`
                    : "Your veterinarian will need to approve this order before fulfillment",
                  status: "active" as Status,
                }),
          },
        ]
      : []),
    {
      key: "prepare",
      label: "Prepare Medication",
      status: hasAllItemsApproved ? "active" : "inactive",
      // Matches logic in ClientCheckoutOrderSerializer.get_coordinates for which address is shown first
      ...(order.has_any_pickup
        ? {
            header: client.primary_contact_email
              ? "We'll email you when your order is ready for pickup"
              : "We'll text you when your order is ready for pickup",
            content: (
              <div className={styles.prepare}>
                {client.primary_contact_email ? (
                  <>
                    <div>
                      Once your medication is ready for pickup, we&apos;ll email you at{" "}
                      <strong>{client.primary_contact_email.email}</strong> with further pickup
                      instructions.
                    </div>
                    <TextButton
                      icon="right"
                      className={styles.link}
                      dest={urls.accounts.detail({
                        search: {
                          form: "updateEmail",
                          visible: ModalStatus.Open,
                        },
                      })}
                    >
                      Change email address
                    </TextButton>
                  </>
                ) : (
                  <>
                    <div>
                      Once your medication is ready for pickup, we&apos;ll text you at{" "}
                      <strong>{client.primary_contact_number?.number_display}</strong> with further
                      pickup instructions.
                    </div>
                    <TextButton
                      icon="right"
                      className={styles.link}
                      dest={urls.accounts.detail({
                        search: {
                          form: "updatePhone",
                          visible: ModalStatus.Open,
                        },
                      })}
                    >
                      Change phone number
                    </TextButton>
                  </>
                )}
              </div>
            ),
          }
        : {
            header: "We'll notify you when your order has been shipped",
            content: (
              <div className={styles.prepare}>
                {client.primary_contact_email ? (
                  <>
                    <div>
                      Once your medication has been shipped, we&apos;ll email you at{" "}
                      <strong>{client.primary_contact_email.email}</strong> with tracking
                      information.
                    </div>
                    <TextButton
                      icon="right"
                      className={styles.link}
                      dest={urls.accounts.detail({
                        search: {
                          form: "updateEmail",
                          visible: ModalStatus.Open,
                        },
                      })}
                    >
                      Change email address
                    </TextButton>
                  </>
                ) : (
                  <>
                    <div>
                      Once your medication has been shipped, we&apos;ll text you at{" "}
                      <strong>{client.primary_contact_number?.number_display}</strong> with tracking
                      information.
                    </div>
                    <TextButton
                      icon="right"
                      className={styles.link}
                      dest={urls.accounts.detail({
                        search: {
                          form: "updatePhone",
                          visible: ModalStatus.Open,
                        },
                      })}
                    >
                      Change phone number
                    </TextButton>
                  </>
                )}
              </div>
            ),
          }),
    },
    {
      key: "fulfillment",
      status: "inactive",
      ...(order.has_any_pickup
        ? {
            label: "Schedule Pickup",
            header: "Pickup medication at front desk reception",
            content: (
              <div className={styles.fulfillment}>
                Once ready, you can pick up your medication during our normal business hours
                {clinic.daily_hours ? `: ${clinic.daily_hours}.` : "."}
                {clinic.primary_address && (
                  <MapBox
                    address={clinic.primary_address}
                    contactInfo={clinic}
                    markers={orderMarkers}
                    markerStyle="paw"
                  />
                )}
              </div>
            ),
          }
        : {
            label: "Items Delivered",
            header: `Once shipped, your ${pluralized(orderItems.length, "item")} will arrive based on the selected ${pluralized(shippingMethods.length, "method")}`,
            content: (
              <div className={styles.fulfillment}>
                {shippingMethods.length === 1 && (
                  <div>
                    <span className={styles.emphasis}>Shipping method: </span>
                    {shippingMethods[0]}
                  </div>
                )}
                {shippingMethods.length > 1 && (
                  <div className={styles.fulfillment}>
                    {Object.entries(shippingMethodToOrderItemDict).map(
                      ([shippingMethod, itemLabels]) => (
                        <div key={shippingMethod}>
                          <div>
                            <span className={styles.emphasis}>{shippingMethod}</span>
                          </div>
                          <ul>
                            {itemLabels.map((itemLabel) => (
                              <li key={itemLabel}>{itemLabel}</li>
                            ))}
                          </ul>
                        </div>
                      ),
                    )}
                  </div>
                )}
                {`Your ${pluralized(orderItems.length, "item")} will be delivered to you at:`}
                {order.shipping_address && (
                  <MapBox
                    address={order.shipping_address}
                    markers={orderMarkers}
                    markerStyle="paw"
                  />
                )}
              </div>
            ),
          }),
    },
  ];

  return <Timeline size="l" events={events} />;
};

const OrderConfirmation = () => {
  const { data: order } = useLastCheckoutQuery();
  const { data: client, isLoading } = useClientQuery();

  if (isLoading) {
    return <LoadingPage />;
  }

  if (!order || !client) {
    return (
      <Page>
        <Navigate dest={urls.home.root()} />
      </Page>
    );
  }

  return (
    <Page className={styles.page}>
      <Body className={styles.body}>
        <div className={styles.container}>
          <div className={styles.heading}>
            <Image className={styles.image} src={checkoutComplete} alt="Dog" />
            <h1 className={styles.header}>Your order has been placed!</h1>
            <p className={styles.subheader}>
              Order Number: <strong>{order.display_number}</strong>
            </p>
          </div>
          <div className={styles.timeline}>
            <OrderTimeline order={order} client={client} />
          </div>
          <div className={styles.actions}>
            <TextButton dest={urls.products.search()}>Browse Product Catalog</TextButton>
            <span className={styles.dot}>·</span>
            <TextButton dest={urls.orders.list()}>View Orders</TextButton>
            <span className={styles.dot}>·</span>
            <TextButton dest={urls.accounts.detail()}>Manage Account</TextButton>
          </div>
        </div>
      </Body>
    </Page>
  );
};

export default OrderConfirmation;
